import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BrandMark from "./BrandMark";

/**
 * Triple-click (or long-press) the "Ep" logo to momentarily reveal the
 * full periodic-tile easter egg: 118 | Ep | 294.21.
 * 118 = total confirmed elements; 294.21 ≈ atomic mass of Oganesson (118).
 */
export default function LogoEasterEgg() {
  const [revealed, setRevealed] = useState(false);
  const clickCount = useRef(0);
  const clickTimer = useRef<number | null>(null);
  const pressTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (clickTimer.current) window.clearTimeout(clickTimer.current);
      if (pressTimer.current) window.clearTimeout(pressTimer.current);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, []);

  const trigger = () => {
    setRevealed(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setRevealed(false), 3200);
  };

  const handleClick = (e: React.MouseEvent) => {
    clickCount.current += 1;
    if (clickCount.current >= 3) {
      e.preventDefault();
      clickCount.current = 0;
      if (clickTimer.current) window.clearTimeout(clickTimer.current);
      trigger();
      return;
    }
    if (clickTimer.current) window.clearTimeout(clickTimer.current);
    clickTimer.current = window.setTimeout(() => {
      clickCount.current = 0;
    }, 500);
  };

  const startPress = () => {
    if (pressTimer.current) window.clearTimeout(pressTimer.current);
    pressTimer.current = window.setTimeout(() => {
      trigger();
    }, 650);
  };
  const endPress = () => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  return (
    <div className="relative">
      <Link
        to="/"
        className="flex items-center gap-2 select-none"
        aria-label="ElementPad - go to homepage"
        onClick={handleClick}
        onMouseDown={startPress}
        onMouseUp={endPress}
        onMouseLeave={endPress}
        onTouchStart={startPress}
        onTouchEnd={endPress}
        onTouchCancel={endPress}
      >
        <BrandMark size={22} />
        <span
          className="text-lg font-semibold tracking-tight text-foreground"
          aria-hidden="true"
        >
          Element<span className="text-primary">Pad</span>
        </span>
      </Link>

      {revealed && (
        <div
          role="status"
          aria-live="polite"
          className="absolute left-0 top-full mt-2 z-50 animate-fade-in"
        >
          <div className="flex items-center gap-3 rounded-xl border border-border bg-popover/95 backdrop-blur-md shadow-lg p-3 pr-4">
            <div
              className="relative w-16 h-16 rounded-lg border border-primary/40 bg-gradient-to-br from-primary/15 to-primary/5 flex flex-col items-center justify-center text-foreground"
              style={{ fontFamily: "var(--font-mono, ui-monospace, monospace)" }}
            >
              <span className="absolute top-1 left-1.5 text-[9px] leading-none text-muted-foreground">
                118
              </span>
              <span className="text-xl font-bold leading-none">Ep</span>
              <span className="absolute bottom-1 right-1.5 text-[8px] leading-none text-muted-foreground">
                294.21
              </span>
            </div>
            <div className="text-xs leading-snug max-w-[200px]">
              <div className="font-semibold text-foreground">
                118 confirmed elements.
              </div>
              <div className="text-muted-foreground">
                Wonder what 119 looks like?
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}